import { useCallback, useMemo } from "react";

import { ExecutorLocations } from "../../services";
import { ExecutorStatus } from "../../services/Executor";
import { RootState, useTypedSelector } from "../../redux/store";
import { useCarParkIdsDecoder, useCarParkFilterAccessControl } from "..";

const parkMergedByItem = new WeakMap<
	ExecutorLocations.Model,
	ExecutorLocations.Model
>();

export type ExecutorLocationSubscribeOptions = {
	taxiServiceIds?: number[];
	limit?: number;
	orderIds?: number[];
	executorWorkingStatus?: boolean;
	executorStatuses?: ExecutorStatus[];
	fleetIds?: number[] | ["all"];
};

export type ExecutorLocationFilterAccess = {
	isUserFleetOwner: boolean;
	accessFleetsIdsOnlyActive?: number[];
	fleetIdsAll: number[];
};

type CarWithPark = { id: number; park?: ExecutorLocations.Model["park"] };

export const mergeParkIntoExecutorLocations = (
	locations: ExecutorLocations.Model[] | null | undefined,
	cars: CarWithPark[] | null | undefined,
	isUserFleetOwner: boolean,
	accessFleetsIdsOnlyActive?: number[],
): ExecutorLocations.Model[] => {
	if (!Array.isArray(locations)) {
		return [];
	}

	if (!Array.isArray(cars)) {
		return locations;
	}

	const carById = new Map(cars.map((car) => [car.id, car]));
	const allowedParks = accessFleetsIdsOnlyActive || [];

	return (
		locations
			.map((item) => {
				const park = carById.get(item.carId)?.park;
				const cached = parkMergedByItem.get(item);
				if (cached && cached.park === park) return cached;
				const merged: ExecutorLocations.Model = {
					...item,
					park,
				};
				parkMergedByItem.set(item, merged);
				return merged;
			})
			.filter((item) =>
				isUserFleetOwner
					? allowedParks.includes(item.park?.id ?? -2)
					: true,
			) || []
	);
};

export const filterExecutorLocations = (
	items: ExecutorLocations.Model[],
	{
		limit,
		taxiServiceIds,
		orderIds,
		executorWorkingStatus,
		executorStatuses,
		fleetIds,
	}: ExecutorLocationSubscribeOptions,
	fleetIdsAll: number[],
): ExecutorLocations.Model[] => {
	if (!Array.isArray(items)) {
		return [];
	}

	let retval = items;

	const isTaxiServiceIds =
		Array.isArray(taxiServiceIds) && taxiServiceIds.length;
	const isLimit = typeof limit === "number";
	const isOrderIds = Array.isArray(orderIds) && orderIds.length;
	const isExecutorWorkingStatus = typeof executorWorkingStatus === "boolean";
	const isExecutorStatuses =
		Array.isArray(executorStatuses) && executorStatuses.length;
	const isFleetIds =
		Array.isArray(fleetIds) &&
		fleetIds.length &&
		fleetIds[0] !== "all" &&
		fleetIds.length !== fleetIdsAll.length;

	if (
		isTaxiServiceIds ||
		isFleetIds ||
		isOrderIds ||
		isExecutorWorkingStatus ||
		isExecutorStatuses
	) {
		retval = items.filter((item) => {
			if (
				isTaxiServiceIds &&
				Number.isFinite(Number(item.taxiServiceId)) &&
				!(
					taxiServiceIds &&
					taxiServiceIds.includes(Number(item.taxiServiceId))
				)
			) {
				return false;
			}

			if (
				isFleetIds &&
				!((fleetIds as number[]) || []).includes(item.park?.id ?? -2)
			) {
				return false;
			}

			if (
				isOrderIds &&
				!(
					orderIds &&
					item.order?.id &&
					orderIds.includes(item.order.id)
				)
			) {
				return false;
			}

			if (
				isExecutorWorkingStatus &&
				item.executor?.isWorking !== executorWorkingStatus
			) {
				return false;
			}

			if (
				isExecutorStatuses &&
				item.executor?.status &&
				!(
					executorStatuses &&
					executorStatuses.includes(item.executor.status)
				)
			) {
				return false;
			}

			return true;
		});
	}

	if (isLimit) {
		return retval.slice(0, limit);
	}

	return retval;
};

export const selectFilteredExecutorLocations = (
	state: RootState,
	filters: ExecutorLocationSubscribeOptions,
	access: ExecutorLocationFilterAccess,
): ExecutorLocations.Model[] =>
	filterExecutorLocations(
		mergeParkIntoExecutorLocations(
			state.globalState.executorLocations.data.subscription.basic,
			state.orders.map.data.cars,
			access.isUserFleetOwner,
			access.accessFleetsIdsOnlyActive,
		),
		filters,
		access.fleetIdsAll,
	);

const useExecutorLocationsSubHandler =
	(): useExecutorLocationsSubHandler.Props => {
		const { isUserFleetOwner, accessFleetsIdsOnlyActive } =
			useCarParkFilterAccessControl<any>();
		const allCarParkIds = useCarParkIdsDecoder();

		const _executorLocationsSubscribe = useTypedSelector(
			(state) =>
				state.globalState.executorLocations.data.subscription.basic,
		);
		const cars = useTypedSelector((state) => state.orders.map.data.cars);

		const fleetIdsAll = useMemo(
			() => allCarParkIds(["all"]),
			[allCarParkIds],
		);

		const executorLocationsSubscribe = useMemo(
			() =>
				mergeParkIntoExecutorLocations(
					_executorLocationsSubscribe,
					cars,
					isUserFleetOwner,
					accessFleetsIdsOnlyActive,
				),
			[
				_executorLocationsSubscribe,
				accessFleetsIdsOnlyActive,
				cars,
				isUserFleetOwner,
			],
		);

		const getAllExecutorLocationsSubHandler = useCallback(
			(
				options: useExecutorLocationsSubHandler.SmallSubscribeOptions,
			): ExecutorLocations.Model[] =>
				filterExecutorLocations(
					executorLocationsSubscribe,
					options,
					fleetIdsAll,
				),
			[executorLocationsSubscribe, fleetIdsAll],
		);

		return useMemo(
			() => ({
				getAllExecutorLocationsSubHandler,
			}),
			[getAllExecutorLocationsSubHandler],
		);
	};

declare namespace useExecutorLocationsSubHandler {
	interface Props {
		getAllExecutorLocationsSubHandler: (
			data: SmallSubscribeOptions,
		) => ExecutorLocations.Model[];
	}

	type SmallSubscribeOptions = ExecutorLocationSubscribeOptions;
}

export default useExecutorLocationsSubHandler;
